import { useState, useEffect, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../popover";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import TextFilter from "../text-filter";
import TableFilterState from "./table-filter-state";
import { debounce } from "lodash";

type PopoverTextFilterProps<T = any> = {
  filterState: TableFilterState<T>;
  placeholder?: string | null;
  title?: string | null;
  description?: string | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEnter?: (value: string) => void;
  debounceMs?: number;
};

const PopoverTextFilter = <T extends Record<string, any>>({
  filterState,
  placeholder,
  title,
  description,
  isOpen,
  onOpenChange,
  onEnter,
  debounceMs = 0,
}: PopoverTextFilterProps<T>) => {
  // Check if filter has value
  const hasValue = filterState.filter[filterState.filterKey] && filterState.filter[filterState.filterKey].trim() !== '';
  const [tempValue, setTempValue] = useState(
    filterState.filter[filterState.filterKey] || ""
  );
  
  // Internal state for popover open/close
  const [internalOpen, setInternalOpen] = useState(false);

  // Update tempValue when filter changes from outside (e.g., when clearing filters)
  useEffect(() => {
    if (onEnter) {
      setTempValue(filterState.filter[filterState.filterKey] || "");
    }
  }, [filterState.filter[filterState.filterKey], onEnter]);

  // Use controlled state if provided, otherwise use internal state
  const controlledOpen = isOpen !== undefined ? isOpen : internalOpen;
  const controlledOnOpenChange = onOpenChange || setInternalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    controlledOnOpenChange(newOpen);
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceSetFilter = (val: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      filterState.setFilter((prev) => ({
        ...prev,
        [filterState.filterKey]: val,
      }));
    }, debounceMs);
  };

  const handleChange = (val: string) => {
    if (onEnter) {
      setTempValue(val);
    } else {
      setTempValue(val);
      debounceSetFilter(val);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onEnter && e.key === "Enter") {
      onEnter(tempValue.trim());
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Popover open={controlledOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <img className="ml-2 w-3 cursor-pointer" src={MagnifyingGlass} alt="Search" />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2">
        <TextFilter
          value={tempValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || `Search...`}
          title={title || null}
          description={description || null}
        />
      </PopoverContent>
    </Popover>
  );
};

export default PopoverTextFilter;
